import {
  action, computed, makeAutoObservable, observable, when,
} from 'mobx';
import { Discount } from '@gamiphy/bl/reward';
import { DiscountsApi } from '@gamiphy/gamix-modules/reward';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import { RewardsAnalytics } from '@gamiphy/loyalty-station-desktop/store/analytics';
import { round } from '@gamiphy/utils/number';
import { isDateSameOrAfter } from '@gamiphy/utils/date';
import { SdkStore } from '@gamiphy/loyalty-station-desktop/store/sdk';
import { DiscountDto } from '@coretava/api/ecommerce-service';
import { DiscountCodesApi, PopulatedDiscountCode } from '@coretava/api/core-service';
import { DiscountsStore } from '../../discounts';
import { AccountStore } from '../../account';

class DiscountStore {
  @observable initialized = false;

  @observable appDiscount?: Discount = undefined;

  @observable discounts?: DiscountDto[] = undefined;

  constructor() {
    makeAutoObservable(this);

    void this.initialize();
  }

  @computed get minGemsToRedeem(): number {
    const bundlesGreaterThanMinGems = this.appDiscount?.bundles?.filter((bundle) => (bundle >= (this.appDiscount?.min.gems || 0))) ?? [];
    return !this.appDiscount?.bundles ? (this.appDiscount?.min?.gems || 0) : Math.min(...bundlesGreaterThanMinGems);
  }

  @computed get maxGemsToRedeem(): number {
    const bundlesLessThanMaxGems = this.appDiscount?.bundles?.filter((bundle) => bundle <= (this.appDiscount?.max.gems ?? Infinity)) ?? [];
    return !this.appDiscount?.bundles ? (this.appDiscount?.max?.gems ?? Infinity) : Math.max(...bundlesLessThanMaxGems);
  }

  @computed get availableGemsToRedeem(): number {
    const userPoints = UserStore.user?.points.gems || 0;

    switch (true) {
      case userPoints > this.maxGemsToRedeem:
        return this.maxGemsToRedeem;
      case userPoints > this.minGemsToRedeem:
        return userPoints;
      default:
        return 0;
    }
  }

  @computed get availableValueToRedeem(): number {
    return this.getGemsValue(this.availableGemsToRedeem);
  }

  @computed get inactive(): boolean {
    return (this.appDiscount?.max?.gems ?? Number.MAX_SAFE_INTEGER) <= 0;
  }

  @computed get discount(): DiscountDto | undefined {
    return this.discounts?.[0];
  }

  @computed get isAutoApply(): boolean {
    switch (AppStore.app.agent) {
      case 'vtex':
      case 'shopify':
        return !AccountStore.account?.yampi;
      default:
        return false;
    }
  }

 @computed
  get discountCodesApi(): DiscountCodesApi {
    return new DiscountCodesApi({
      isJsonMime(): boolean {
        return true;
      },
      basePath: process.env.BASE_API_URL,
      accessToken: UserStore.token,
    });
  }

  @action
 public async initialize(): Promise<void> {
   await when(() => AppStore.initialized);

   const [appDiscount] = await DiscountsApi.getDiscounts();
   this.appDiscount = appDiscount;

   await when(() => UserStore.isLoggedIn);

   if (UserStore.user) {
     const { data: { discounts } } = await DiscountsStore.api.discountsControllerList(AppStore.app.id, 'valid');
     const discountCodes = discounts.sort((a, b) => (isDateSameOrAfter(new Date(a.createdAt), new Date(b.createdAt)) ? -1 : 1));
     this.discounts = discountCodes;
   }
   this.initialized = true;
 }

  @action
  public async redeemDiscount(gems: number): Promise<PopulatedDiscountCode> {
    if (this.appDiscount) {
      const response = await this.discountCodesApi.discountCodesControllerCreate(
        AppStore.app.id,
        {
          discount: this.appDiscount.id,
          points: { gems },
        },
      );
      if (response.data.discountCode.eDiscount) {
        this.appendDiscount(response.data.discountCode.eDiscount);
      }
      RewardsAnalytics.trackDiscountCodeRedeemed({ value: response.data.discountCode.eDiscount?.value ?? 0, gems });
      return response.data.discountCode;
    }
    throw new Error('Discount is not defined');
  }

  @action
  public async applyDiscountCode(code: string): Promise<string | undefined> {
    if (!this.appDiscount) return undefined;

    await SdkStore.cart?.applyDiscount(code);

    return SdkStore.cart?.data?.id;
  }

  @action
  appendDiscount(discount: DiscountDto): void {
    this.discounts = [
      discount,
      ...(this.discounts ?? []),
    ];
  }

  getGemsValue(gems: number): number {
    return round((this.appDiscount?.value || 0) * gems, 2);
  }

  isAvailableToRedeem(discountValue: number): boolean {
    return !discountValue || (!!this.minGemsToRedeem && (discountValue < this.minGemsToRedeem)) || (
      this.appDiscount && (UserStore.user?.points.gems ?? 0) * (this.appDiscount?.value ?? 0) < discountValue) || false;
  }
}

export default new DiscountStore();
